import { getOfflineDb, type QueuedOrder, type QueuedOrderStatus } from "./db";

export type { QueuedOrder, QueuedOrderStatus } from "./db";

export type QueueChangeListener = () => void;

const listeners = new Set<QueueChangeListener>();

function notifyChange() {
  for (const listener of listeners) {
    try {
      listener();
    } catch (err) {
      console.error("[offline queue] listener threw", err);
    }
  }
}

export function subscribeQueue(listener: QueueChangeListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export interface EnqueueParams {
  idempotencyKey: string;
  payload: unknown;
}

export async function enqueueOrder(
  params: EnqueueParams,
): Promise<QueuedOrder> {
  const db = await getOfflineDb();
  const now = Date.now();
  const entry: QueuedOrder = {
    id: params.idempotencyKey,
    idempotencyKey: params.idempotencyKey,
    payload: params.payload,
    createdAt: now,
    attemptCount: 0,
    lastAttemptAt: null,
    status: "pending",
    error: null,
    serverOrderId: null,
  };
  await db.put("pending_orders", entry);
  notifyChange();

  return entry;
}

export async function listQueue(): Promise<QueuedOrder[]> {
  const db = await getOfflineDb();
  const all = await db.getAllFromIndex("pending_orders", "by-created");

  return all;
}

export async function getQueued(id: string): Promise<QueuedOrder | undefined> {
  const db = await getOfflineDb();

  return db.get("pending_orders", id);
}

export async function getPendingCount(): Promise<number> {
  const db = await getOfflineDb();
  const pending = await db.countFromIndex(
    "pending_orders",
    "by-status",
    "pending",
  );
  const failed = await db.countFromIndex(
    "pending_orders",
    "by-status",
    "failed",
  );

  return pending + failed;
}

export async function markStatus(
  id: string,
  status: QueuedOrderStatus,
  patch?: Partial<
    Pick<
      QueuedOrder,
      "error" | "serverOrderId" | "attemptCount" | "lastAttemptAt"
    >
  >,
): Promise<void> {
  const db = await getOfflineDb();
  const current = await db.get("pending_orders", id);
  if (!current) return;
  const next: QueuedOrder = {
    ...current,
    status,
    error: patch?.error ?? (status === "failed" ? current.error : null),
    serverOrderId: patch?.serverOrderId ?? current.serverOrderId,
    attemptCount: patch?.attemptCount ?? current.attemptCount,
    lastAttemptAt: patch?.lastAttemptAt ?? current.lastAttemptAt,
  };
  await db.put("pending_orders", next);
  notifyChange();
}

export async function removeFromQueue(id: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete("pending_orders", id);
  notifyChange();
}

export async function clearSynced(): Promise<number> {
  const db = await getOfflineDb();
  const tx = db.transaction("pending_orders", "readwrite");
  const index = tx.store.index("by-status");
  const synced = await index.getAllKeys("synced");
  await Promise.all(synced.map((key) => tx.store.delete(key)));
  await tx.done;
  if (synced.length > 0) notifyChange();

  return synced.length;
}
